import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  MICROLINK_DEBOUNCE_MS,
  MICROLINK_STATUS_CLEAR_MS,
} from "@/lib/constants";

const todayISO = () => new Date().toISOString().slice(0, 10);

const URL_LOOKS_VALID = /^https?:\/\//i;
const URL_LOOKS_DOMAIN = /\w+\.\w+/;

const emptyEntry = {
  name: "",
  url: "",
  type: "Website",
  status: "Live",
  description: "",
  platform: "",
  note: "",
  date: todayISO(),
};

function normalizeUrl(raw) {
  const url = (raw || "").trim();
  if (!url) return "";
  if (URL_LOOKS_VALID.test(url)) return url;
  return "https://" + url;
}

function shouldFetchMicrolink(form) {
  const url = (form.url || "").trim();
  if (!url) return false;
  if (!URL_LOOKS_VALID.test(url) && !URL_LOOKS_DOMAIN.test(url)) return false;
  // Skip if both name and description already filled
  if ((form.name || "").trim() && (form.description || "").trim()) return false;
  return true;
}

function mergeMicrolink(form, data) {
  const name = form.name && form.name.trim() ? form.name : data.title || form.name;
  const description =
    form.description && form.description.trim()
      ? form.description
      : data.description || form.description;
  const platform =
    form.platform && form.platform.trim()
      ? form.platform
      : data.publisher || form.platform;
  return { ...form, name, description, platform };
}

function getSubmitLabel(saving, isEdit) {
  if (saving) return "Speichere …";
  if (isEdit) return "Speichern";
  return "Anlegen";
}

export default function EntryModal({ initial, onClose, onSave, types, statuses }) {
  const [form, setForm] = useState(() => ({ ...emptyEntry, ...(initial || {}) }));
  const [ml, setMl] = useState({ loading: false, msg: "" });
  const [saving, setSaving] = useState(false);
  const fetchedFor = useRef("");

  // Escape key closes modal
  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Auto-fetch microlink when URL changes
  useEffect(() => {
    if (!shouldFetchMicrolink(form)) return undefined;
    const url = form.url.trim();
    if (fetchedFor.current === url) return undefined;
    fetchedFor.current = url;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setMl({ loading: true, msg: "Hole Vorschau …" });
        const { data } = await api.get("/microlink", { params: { url } });
        if (cancelled) return;
        if (data && data.ok) {
          setForm((f) => mergeMicrolink(f, data));
          setMl({ loading: false, msg: "✓ Vorgefüllt aus der Seite" });
          setTimeout(
            () => setMl((m) => ({ ...m, msg: "" })),
            MICROLINK_STATUS_CLEAR_MS
          );
        } else {
          setMl({ loading: false, msg: "" });
        }
      } catch {
        setMl({ loading: false, msg: "" });
      }
    }, MICROLINK_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.url]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, url: normalizeUrl(form.url) };
    await onSave(payload);
    setSaving(false);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const isEdit = !!initial?.id;
  const submitLabel = getSubmitLabel(saving, isEdit);

  return (
    <div className="modal-backdrop" onMouseDown={handleBackdropClick}>
      <div className="modal" data-testid="entry-modal">
        <div className="modal-header">
          <h2>{isEdit ? "Eintrag bearbeiten" : "Neuer Eintrag"}</h2>
          <button className="icon-btn" onClick={onClose} title="Schließen" aria-label="Schließen">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="field">
              <label>
                URL <span className="hint">— wird zum Holen von Titel/Beschreibung verwendet</span>
              </label>
              <input
                type="text"
                value={form.url}
                onChange={(e) => setField("url", e.target.value)}
                placeholder="https://beispiel.de"
                data-testid="modal-url"
                autoFocus
              />
              {ml.msg && (
                <div className="microlink-status" data-testid="microlink-status">
                  {ml.msg}
                </div>
              )}
            </div>
            <div className="field">
              <label>Name / Projekt</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="z. B. BDV Website"
                data-testid="modal-name"
              />
            </div>
            <div className="field-row">
              <div className="field">
                <label>Typ</label>
                <select
                  value={form.type}
                  onChange={(e) => setField("type", e.target.value)}
                  data-testid="modal-type"
                >
                  {types.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setField("status", e.target.value)}
                  data-testid="modal-status"
                >
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field">
              <label>Beschreibung</label>
              <textarea
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="Kurz: was läuft drauf / was wurde gemacht"
                data-testid="modal-description"
              />
            </div>
            <div className="field-row">
              <div className="field">
                <label>Plattform</label>
                <input
                  type="text"
                  value={form.platform}
                  onChange={(e) => setField("platform", e.target.value)}
                  placeholder="z. B. Vercel, Emergent.sh, eigene Domain"
                  data-testid="modal-platform"
                />
              </div>
              <div className="field">
                <label>Datum</label>
                <input
                  type="date"
                  value={form.date || ""}
                  onChange={(e) => setField("date", e.target.value)}
                  data-testid="modal-date"
                />
              </div>
            </div>
            <div className="field">
              <label>
                Notiz <span className="hint">— optional, z. B. Zugangs-Hinweise</span>
              </label>
              <textarea
                value={form.note}
                onChange={(e) => setField("note", e.target.value)}
                placeholder="Frei für Notizen / Zugänge"
                data-testid="modal-note"
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>
              Abbrechen
            </button>
            <button
              type="submit"
              className="btn btn-gold"
              disabled={saving}
              data-testid="modal-save"
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
